import { useEffect, useMemo } from 'react';
import Glossary from '@/components/common/Glossary';
import GlossaryPagination from '@/components/common/GlossaryPagination';
import MetaTagsMillesima from '@/components/layout/MetaTagsMillesima';
import { PAGE_TYPE } from '@/constants/datalayer';
import { CategModel, HrefLangModel } from '@/models';
import { useServices } from '@/services/useServices';
import styles from './CategListingView.module.scss';

const updateCategList = (categList) => {
	const index = [
		'0-9',
		'A',
		'B',
		'C',
		'D',
		'E',
		'F',
		'G',
		'H',
		'I',
		'J',
		'K',
		'L',
		'M',
		'N',
		'O',
		'P',
		'Q',
		'R',
		'S',
		'T',
		'U',
		'V',
		'W',
		'X',
		'Y',
		'Z',
	];

	const initList = index.map((letter) => ({ letter, list: [] }));

	return categList.reduce((finalList, c) => {
		const letter = index.includes(c.name[0]) ? c.name[0] : '0-9';
		const letterIndex = index.indexOf(letter);
		const element = finalList[letterIndex];
		element.list.push(c);
		if (!element.id) {
			element.id = letter;
		}
		finalList[letterIndex] = element;
		return finalList;
	}, initList);
};

type CategListingViewProps = {
	name: string;
	title: string;
	metaDesc: string;
	categList: CategModel[];
	url: string;
	hrefLang: HrefLangModel[];
};

export const CategListingView = ({ name, title, metaDesc, categList, url, hrefLang }: CategListingViewProps) => {
	const categs = useMemo(() => updateCategList(categList), [categList]);
	const anchors = categs.map(({ letter, id }) => ({ name: letter, id }));
	const { datalayerService } = useServices();

	useEffect(() => {
		datalayerService.pushPageName(PAGE_TYPE.CATEG_LISTING, PAGE_TYPE.CATEG_LISTING);
	}, []);

	return (
		<>
			<MetaTagsMillesima title={title} metaDesc={metaDesc} originalUrl={url} hrefLang={hrefLang} categListing />

			<div className={`container-fluid ${styles['categ-listing']} ${styles['container-width']}`}>
				<h1>{name}</h1>
				{metaDesc && <p>{metaDesc}</p>}
				<GlossaryPagination anchors={anchors} />
				<Glossary sections={categs} />
			</div>
		</>
	);
};

export default CategListingView;

/**
 * Prefetch function  called on server side
 * @param {object} _ctx The server side context
 * @param {object} store The server side application store
 * @param {object} props The seo page props
 * @return {object} Return an object containing the component props
 *    layoutOption : object containing the needed page layout (see _app component)
 *    namespaces : an array containing the needed translation namespaces
 */
export const prefetchView = async (_ctx, { getState, services }, props) => {
	const { title, metaDesc, categType, filter, url, hrefLang } = props;
	const {
		root: { storeConf },
	} = getState();

	const categList = await services.productsService.getListingCateg(storeConf.storeId, categType, filter);

	return {
		layoutOptions: {
			withScrollButton: true,
		},
		storeConf,
		url,
		hrefLang,
		categList,
		metaDesc,
		title,
	};
};
